"use client";

import { useState, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Download,
  Save,
  FileText,
  User,
  TrendingUp,
  Target,
  Award,
  Heart,
  CheckCircle,
  AlertCircle,
  BarChart3,
  RefreshCw,
} from "lucide-react";
import "./DetailedComparisonPage.css";
import BackButton from "../shared/backButton";
import Modal from "../layout/Modal";

import TeamTeamReport from "./detailed-reports/team-team";
import DepartmentDepartmentReport from "./detailed-reports/department-department-detailed";
import UserUserReport from "./detailed-reports/user-user-detailed";
import UserTeamReport from "./detailed-reports/user-team-detailed-report";
import UserRoleReport from "./detailed-reports/user-role-detailed-report";

const DetailedComparisonPage = () => {
  const location = useLocation();
  const comparisonDataRoute = location.state?.comparisonData; // <-- read it here
  console.log(comparisonDataRoute, "comparisonDataRoute");

  const [activeSection, setActiveSection] = useState("overview");
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showFilenameModal, setShowFilenameModal] = useState(false);
  const [filename, setFilename] = useState("");
  const [reportType, setReportType] = useState("");

  useEffect(() => {
    // console.log(
    //   "DetailedComparisonPage mounted with comparisonData:",
    //   comparisonDataRoute
    // );
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get("id");
    setReportType(id || "");
  }, []);

  const getReportTitle = () => {
    switch (reportType) {
      case "tt1":
        return "Skill Gap Analysis Report";
      case "tt2":
        return "Personality Distribution Report";
      case "tt3":
        return "Leadership Metrics Report";
      default:
        return getDetailedAnalysis().title;
    }
  };

  const renderDetailedReport = () => {
  switch (reportType) {
    case "tt1":
    case "tt2":
    case "tt3":
      return <TeamTeamReport comparisonData={comparisonDataRoute} />;
    case "dd1":
    case "dd2":
    case "dd3":
      return <DepartmentDepartmentReport comparisonData={comparisonDataRoute} />;
    case "uu1":
    case "uu2":
    case "uu3":
      return <UserUserReport comparisonData={comparisonDataRoute} />;
    case "ut1":
    case "ut2":
    case "ut3":
      return <UserTeamReport comparisonData={comparisonDataRoute} />;
    case "ur1":
    case "ur2":
    case "ur3":
      return <UserRoleReport comparisonData={comparisonDataRoute} />;
    default:
      // Show default analysis if no URL parameter or unrecognized parameter
      if (!comparisonDataRoute || !comparisonDataRoute.entities) {
        return renderDefaultAnalysis();
      }

      const entityTypes = comparisonDataRoute.entities
        .map((e) => e.type)
        .sort()
        .join("-");

      if (entityTypes === "team-team") {
        const analysisType =
          comparisonDataRoute.analysisType || "skill-gap";

        return <TeamTeamReport comparisonData={comparisonDataRoute} />;
      }

      return renderDefaultAnalysis();
  }
};

  const getDetailedAnalysis = () => {
    if (!comparisonDataRoute || !comparisonDataRoute.entities) {
      return getDefaultAnalysis();
    }

    const entityTypes = comparisonDataRoute.entities.map((e) => e.type);
    const hasEmployee = entityTypes.includes("employee");
    const hasRole = entityTypes.includes("role");
    const hasTeam = entityTypes.includes("team");

    if (hasEmployee && hasRole) {
      return getEmployeeRoleAnalysis(comparisonDataRoute.entities);
    } else if (hasTeam) {
      return getTeamAnalysis(comparisonDataRoute.entities);
    } else {
      return getGeneralAnalysis(comparisonDataRoute.entities);
    }
  };

  // ... existing analysis functions remain the same ...

  const getEmployeeRoleAnalysis = (entities) => {
    const employee = entities.find((e) => e.type === "employee");
    const role = entities.find((e) => e.type === "role");

    return {
      type: "Employee vs Role",
      title: `${employee?.data.name} - ${role?.data.name} Match Analysis`,
      jobMatchScore: 87,
      sections: {
        jobMatch: {
          title: "Job Match Analysis",
          score: 87,
          details: [
            { factor: "Technical Skills", score: 92, status: "excellent" },
            { factor: "Experience Level", score: 85, status: "good" },
            { factor: "Industry Knowledge", score: 78, status: "good" },
            { factor: "Role Requirements", score: 90, status: "excellent" },
          ],
        },
        competencies: {
          title: "Core Competencies Comparison",
          categories: [
            {
              name: "Adaptability & Learning",
              employeeScore: 88,
              roleRequirement: 85,
              status: "exceeds",
              details:
                "Strong ability to learn new technologies and adapt to changing requirements",
            },
            {
              name: "Leadership & Teamwork",
              employeeScore: 82,
              roleRequirement: 90,
              status: "below",
              details:
                "Good team collaboration but needs development in leadership areas",
            },
            {
              name: "Work Ethic & Reliability",
              employeeScore: 95,
              roleRequirement: 85,
              status: "exceeds",
              details:
                "Exceptional work ethic with consistent delivery and reliability",
            },
            {
              name: "Problem Solving",
              employeeScore: 89,
              roleRequirement: 88,
              status: "meets",
              details:
                "Strong analytical skills with creative problem-solving approach",
            },
            {
              name: "Creativity & Innovation",
              employeeScore: 76,
              roleRequirement: 80,
              status: "below",
              details:
                "Good creative thinking but could benefit from innovation training",
            },
            {
              name: "Attention to Detail",
              employeeScore: 91,
              roleRequirement: 85,
              status: "exceeds",
              details:
                "Meticulous attention to detail with high quality output",
            },
          ],
        },
        profile: {
          employee: {
            name: employee?.data.name || "Sarah Johnson",
            role: employee?.data.role || "Senior Developer",
            experience: employee?.data.experience || "5 years",
            performance: employee?.data.performance || 95,
            skills: [
              "React",
              "TypeScript",
              "Node.js",
              "AWS",
              "Team Leadership",
            ],
            achievements: [
              "Led successful migration to microservices architecture",
              "Mentored 3 junior developers",
              "Reduced application load time by 40%",
            ],
            strengths: [
              "Technical expertise",
              "Problem solving",
              "Reliability",
            ],
            growthAreas: [
              "Public speaking",
              "Strategic planning",
              "Innovation leadership",
            ],
          },
          role: {
            name: role?.data.name || "Technical Lead",
            department: "Engineering",
            level: "Senior",
            requiredSkills: [
              "React",
              "System Design",
              "Team Management",
              "Architecture",
            ],
            responsibilities: [
              "Lead technical decisions and architecture",
              "Mentor team members and drive best practices",
              "Collaborate with product and design teams",
            ],
            requirements: [
              "5+ years experience",
              "Leadership experience",
              "Strong technical skills",
            ],
          },
        },
        softSkills: {
          title: "Soft Skills Analysis",
          skills: [
            {
              name: "Communication",
              score: 85,
              benchmark: 80,
              trend: "improving",
            },
            {
              name: "Emotional Intelligence",
              score: 78,
              benchmark: 75,
              trend: "stable",
            },
            {
              name: "Time Management",
              score: 92,
              benchmark: 85,
              trend: "excellent",
            },
            {
              name: "Conflict Resolution",
              score: 72,
              benchmark: 80,
              trend: "needs_improvement",
            },
            { name: "Mentoring", score: 88, benchmark: 85, trend: "strong" },
          ],
          strengths: [
            "Excellent time management and organizational skills",
            "Strong technical communication abilities",
            "Natural mentoring and knowledge sharing",
          ],
          weaknesses: [
            "Could improve conflict resolution techniques",
            "Needs development in strategic communication",
            "Public speaking confidence could be enhanced",
          ],
        },
        summary: `${
          employee?.data.name || "Sarah Johnson"
        } demonstrates strong technical capabilities and work ethic that align well with the ${
          role?.data.name || "Technical Lead"
        } position. With an overall match score of 87%, she shows particular strength in technical skills, reliability, and attention to detail. Key development areas include leadership skills and creative innovation. With targeted development in leadership and strategic thinking, she would be an excellent fit for this role. Recommended next steps include leadership training, cross-functional project experience, and mentorship opportunities.`,
      },
    };
  };

  const getTeamAnalysis = (entities) => {
    return {
      type: "Team Comparison",
      title: "Team Performance & Structure Analysis",
      sections: {
        performance: {
          title: "Performance Metrics Comparison",
          teams: entities.map((entity) => ({
            name: entity.data.name,
            performance: entity.data.performance || 85,
            members: entity.data.members || 6,
            satisfaction: entity.data.satisfaction || 4.2,
            efficiency: Math.floor(Math.random() * 20) + 80,
          })),
        },
        structure: {
          title: "Team Structure Analysis",
          comparison:
            "Detailed analysis of team composition, roles, and collaboration patterns",
        },
        summary:
          "Comprehensive team comparison revealing strengths and optimization opportunities.",
      },
    };
  };

  const getGeneralAnalysis = (entities) => {
  return {
    type: "General Comparison",
    title: "Comparative Analysis",
    sections: {
      metrics: {
        title: "Key Metrics Comparison",
        entities: entities.map((entity) => {
          // Support both shapes:
          // 1) { type, data: { name, performance/avgPerformance } }
          // 2) { type, name, performanceAvg/avgPerformance/performance }
          const data = entity.data || entity;

          return {
            name: data.name || "",
            type: entity.type || data.type || "",
            performance:
              (data.performance ??
               data.avgPerformance ??
               data.performanceAvg ??
               85),
          };
        }),
      },
      summary: "Detailed comparative analysis of selected entities.",
    },
  };
};


  const getDefaultAnalysis = () => {
    return {
      type: "Sample Analysis",
      title: "Sarah Johnson - Technical Lead Match Analysis",
      jobMatchScore: 87,
      sections: {
        jobMatch: {
          title: "Job Match Analysis",
          score: 87,
          details: [
            { factor: "Technical Skills", score: 92, status: "excellent" },
            { factor: "Experience Level", score: 85, status: "good" },
            { factor: "Industry Knowledge", score: 78, status: "good" },
            { factor: "Role Requirements", score: 90, status: "excellent" },
          ],
        },
        competencies: {
          title: "Core Competencies Comparison",
          categories: [
            {
              name: "Adaptability & Learning",
              employeeScore: 88,
              roleRequirement: 85,
              status: "exceeds",
              details:
                "Strong ability to learn new technologies and adapt to changing requirements",
            },
            {
              name: "Leadership & Teamwork",
              employeeScore: 82,
              roleRequirement: 90,
              status: "below",
              details:
                "Good team collaboration but needs development in leadership areas",
            },
            {
              name: "Work Ethic & Reliability",
              employeeScore: 95,
              roleRequirement: 85,
              status: "exceeds",
              details:
                "Exceptional work ethic with consistent delivery and reliability",
            },
            {
              name: "Problem Solving",
              employeeScore: 89,
              roleRequirement: 88,
              status: "meets",
              details:
                "Strong analytical skills with creative problem-solving approach",
            },
            {
              name: "Creativity & Innovation",
              employeeScore: 76,
              roleRequirement: 80,
              status: "below",
              details:
                "Good creative thinking but could benefit from innovation training",
            },
            {
              name: "Attention to Detail",
              employeeScore: 91,
              roleRequirement: 85,
              status: "exceeds",
              details:
                "Meticulous attention to detail with high quality output",
            },
          ],
        },
        profile: {
          employee: {
            name: "Sarah Johnson",
            role: "Senior Developer",
            experience: "5 years",
            performance: 95,
            skills: [
              "React",
              "TypeScript",
              "Node.js",
              "AWS",
              "Team Leadership",
            ],
            achievements: [
              "Led successful migration to microservices architecture",
              "Mentored 3 junior developers",
              "Reduced application load time by 40%",
            ],
            strengths: [
              "Technical expertise",
              "Problem solving",
              "Reliability",
            ],
            growthAreas: [
              "Public speaking",
              "Strategic planning",
              "Innovation leadership",
            ],
          },
          role: {
            name: "Technical Lead",
            department: "Engineering",
            level: "Senior",
            requiredSkills: [
              "React",
              "System Design",
              "Team Management",
              "Architecture",
            ],
            responsibilities: [
              "Lead technical decisions and architecture",
              "Mentor team members and drive best practices",
              "Collaborate with product and design teams",
            ],
            requirements: [
              "5+ years experience",
              "Leadership experience",
              "Strong technical skills",
            ],
          },
        },
        softSkills: {
          title: "Soft Skills Analysis",
          skills: [
            {
              name: "Communication",
              score: 85,
              benchmark: 80,
              trend: "improving",
            },
            {
              name: "Emotional Intelligence",
              score: 78,
              benchmark: 75,
              trend: "stable",
            },
            {
              name: "Time Management",
              score: 92,
              benchmark: 85,
              trend: "excellent",
            },
            {
              name: "Conflict Resolution",
              score: 72,
              benchmark: 80,
              trend: "needs_improvement",
            },
            { name: "Mentoring", score: 88, benchmark: 85, trend: "strong" },
          ],
          strengths: [
            "Excellent time management and organizational skills",
            "Strong technical communication abilities",
            "Natural mentoring and knowledge sharing",
          ],
          weaknesses: [
            "Could improve conflict resolution techniques",
            "Needs development in strategic communication",
            "Public speaking confidence could be enhanced",
          ],
        },
        summary: `Sarah Johnson demonstrates strong technical capabilities and work ethic that align well with the Technical Lead position. With an overall match score of 87%, she shows particular strength in technical skills, reliability, and attention to detail. Key development areas include leadership skills and creative innovation. With targeted development in leadership and strategic thinking, she would be an excellent fit for this role. Recommended next steps include leadership training, cross-functional project experience, and mentorship opportunities.`,
      },
    };
  };

  // ... existing renderDefaultAnalysis function remains the same ...

  const renderDefaultAnalysis = () => {
    const analysisData = getDetailedAnalysis();

    const sections = [
      { id: "overview", label: "Overview", icon: BarChart3 },
      { id: "jobMatch", label: "Job Match", icon: Target },
      { id: "competencies", label: "Competencies", icon: Award },
      { id: "profile", label: "Profile Details", icon: User },
      { id: "softSkills", label: "Soft Skills", icon: Heart },
      { id: "summary", label: "Summary", icon: FileText },
    ];

    const getStatusColor = (status) => {
      switch (status) {
        case "excellent":
          return "detailed-status-excellent";
        case "good":
          return "detailed-status-good";
        case "meets":
          return "detailed-status-meets";
        case "exceeds":
          return "detailed-status-exceeds";
        case "below":
          return "detailed-status-below";
        default:
          return "detailed-status-average";
      }
    };

    const getTrendIcon = (trend) => {
      switch (trend) {
        case "improving":
        case "excellent":
        case "strong":
          return <TrendingUp size={14} className="detailed-trend-up" />;
        case "needs_improvement":
          return <AlertCircle size={14} className="detailed-trend-down" />;
        default:
          return <CheckCircle size={14} className="detailed-trend-stable" />;
      }
    };

    const renderSection = () => {
      switch (activeSection) {
        case "overview":
          return (
            <div className="detailed-section-content">
              <div className="detailed-overview-grid">
                <div className="detailed-overview-card detailed-primary">
                  <div className="detailed-overview-icon">
                    <Target size={32} />
                  </div>
                  <div className="detailed-overview-content">
                    <h3>Overall Match Score</h3>
                    <div className="detailed-score-display">
                      {analysisData.jobMatchScore}%
                    </div>
                    <p>Strong alignment with role requirements</p>
                  </div>
                </div>
                <div className="detailed-overview-card detailed-secondary">
                  <div className="detailed-overview-icon">
                    <Award size={32} />
                  </div>
                  <div className="detailed-overview-content">
                    <h3>Key Strengths</h3>
                    <div className="detailed-score-display">6</div>
                    <p>Core competencies identified</p>
                  </div>
                </div>
                <div className="detailed-overview-card detailed-tertiary">
                  <div className="detailed-overview-icon">
                    <TrendingUp size={32} />
                  </div>
                  <div className="detailed-overview-content">
                    <h3>Growth Areas</h3>
                    <div className="detailed-score-display">3</div>
                    <p>Development opportunities</p>
                  </div>
                </div>
              </div>
              <div className="detailed-analysis-summary">
                <h3>Analysis Overview</h3>
                <p>
                  This comprehensive analysis evaluates the compatibility
                  between the candidate and role across multiple dimensions
                  including technical skills, soft skills, experience, and
                  cultural fit. The assessment provides actionable insights for
                  decision-making and development planning.
                </p>
              </div>
            </div>
          );

        case "jobMatch":
          return (
            <div className="detailed-section-content">
              <div className="detailed-job-match-header">
                <h3>Job Match Analysis</h3>
                <div className="detailed-match-score">
                  <span className="detailed-score-label">Match Score:</span>
                  <span className="detailed-score-value">
                    {analysisData.sections.jobMatch?.score}%
                  </span>
                </div>
              </div>
              <div className="detailed-match-factors">
                {analysisData.sections.jobMatch?.details.map(
                  (factor, index) => (
                    <div key={index} className="detailed-factor-item">
                      <div className="detailed-factor-info">
                        <span className="detailed-factor-name">
                          {factor.factor}
                        </span>
                        <span
                          className={`detailed-factor-status ${getStatusColor(
                            factor.status
                          )}`}
                        >
                          {factor.status}
                        </span>
                      </div>
                      <div className="detailed-factor-bar">
                        <div
                          className="detailed-factor-fill"
                          style={{ width: `${factor.score}%` }}
                        ></div>
                        <span className="detailed-factor-score">
                          {factor.score}%
                        </span>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          );

        case "competencies":
          return (
            <div className="detailed-section-content">
              <h3>Core Competencies Comparison</h3>
              <div className="detailed-competencies-grid">
                {analysisData.sections.competencies?.categories.map(
                  (competency, index) => (
                    <div key={index} className="detailed-competency-card">
                      <div className="detailed-competency-header">
                        <h4>{competency.name}</h4>
                        <span
                          className={`detailed-competency-status ${getStatusColor(
                            competency.status
                          )}`}
                        >
                          {competency.status}
                        </span>
                      </div>
                      <div className="detailed-competency-scores">
                        <div className="detailed-score-comparison">
                          <div className="detailed-score-item">
                            <span className="detailed-score-label">
                              Employee
                            </span>
                            <span className="detailed-score-value">
                              {competency.employeeScore}%
                            </span>
                          </div>
                          <div className="detailed-score-item">
                            <span className="detailed-score-label">
                              Required
                            </span>
                            <span className="detailed-score-value">
                              {competency.roleRequirement}%
                            </span>
                          </div>
                        </div>
                        <div className="detailed-competency-bars">
                          <div className="detailed-bar-container">
                            <div className="detailed-bar detailed-employee-bar">
                              <div
                                className="detailed-bar-fill"
                                style={{
                                  width: `${competency.employeeScore}%`,
                                }}
                              ></div>
                            </div>
                            <div className="detailed-bar detailed-requirement-bar">
                              <div
                                className="detailed-bar-fill"
                                style={{
                                  width: `${competency.roleRequirement}%`,
                                }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <p className="detailed-competency-details">
                        {competency.details}
                      </p>
                    </div>
                  )
                )}
              </div>
            </div>
          );

        case "profile":
          return (
            <div className="detailed-section-content">
              <div className="detailed-profile-grid">
                <div className="detailed-profile-card">
                  <div className="detailed-profile-header">
                    <User size={24} />
                    <h3>Employee Profile</h3>
                  </div>
                  <div className="detailed-profile-info">
                    <div className="detailed-profile-basic">
                      <h4>{analysisData.sections.profile?.employee.name}</h4>
                      <p>{analysisData.sections.profile?.employee.role}</p>
                      <span className="detailed-experience">
                        {analysisData.sections.profile?.employee.experience}{" "}
                        experience
                      </span>
                    </div>
                    <div className="detailed-profile-performance">
                      <span className="detailed-performance-label">
                        Performance Score
                      </span>
                      <span className="detailed-performance-value">
                        {analysisData.sections.profile?.employee.performance}%
                      </span>
                    </div>
                  </div>
                  <div className="detailed-profile-section">
                    <h5>Key Skills</h5>
                    <div className="detailed-skills-tags">
                      {analysisData.sections.profile?.employee.skills.map(
                        (skill, index) => (
                          <span key={index} className="detailed-skill-tag">
                            {skill}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                  <div className="detailed-profile-section">
                    <h5>Recent Achievements</h5>
                    <ul className="detailed-achievements-list">
                      {analysisData.sections.profile?.employee.achievements.map(
                        (achievement, index) => (
                          <li key={index}>{achievement}</li>
                        )
                      )}
                    </ul>
                  </div>
                  <div className="detailed-profile-section">
                    <h5>Strengths</h5>
                    <div className="detailed-strengths-list">
                      {analysisData.sections.profile?.employee.strengths.map(
                        (strength, index) => (
                          <span key={index} className="detailed-strength-item">
                            <CheckCircle size={14} />
                            {strength}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                  <div className="detailed-profile-section">
                    <h5>Growth Areas</h5>
                    <div className="detailed-growth-list">
                      {analysisData.sections.profile?.employee.growthAreas.map(
                        (area, index) => (
                          <span key={index} className="detailed-growth-item">
                            <TrendingUp size={14} />
                            {area}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                </div>

                <div className="detailed-profile-card">
                  <div className="detailed-profile-header">
                    <Target size={24} />
                    <h3>Role Profile</h3>
                  </div>
                  <div className="detailed-profile-info">
                    <div className="detailed-profile-basic">
                      <h4>{analysisData.sections.profile?.role.name}</h4>
                      <p>{analysisData.sections.profile?.role.department}</p>
                      <span className="detailed-experience">
                        {analysisData.sections.profile?.role.level} Level
                      </span>
                    </div>
                  </div>
                  <div className="detailed-profile-section">
                    <h5>Required Skills</h5>
                    <div className="detailed-skills-tags">
                      {analysisData.sections.profile?.role.requiredSkills.map(
                        (skill, index) => (
                          <span
                            key={index}
                            className="detailed-skill-tag detailed-required"
                          >
                            {skill}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                  <div className="detailed-profile-section">
                    <h5>Key Responsibilities</h5>
                    <ul className="detailed-responsibilities-list">
                      {analysisData.sections.profile?.role.responsibilities.map(
                        (responsibility, index) => (
                          <li key={index}>{responsibility}</li>
                        )
                      )}
                    </ul>
                  </div>
                  <div className="detailed-profile-section">
                    <h5>Requirements</h5>
                    <ul className="detailed-requirements-list">
                      {analysisData.sections.profile?.role.requirements.map(
                        (requirement, index) => (
                          <li key={index}>{requirement}</li>
                        )
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          );

        case "softSkills":
          return (
            <div className="detailed-section-content">
              <h3>Soft Skills Analysis</h3>
              <div className="detailed-soft-skills-grid">
                {analysisData.sections.softSkills?.skills.map(
                  (skill, index) => (
                    <div key={index} className="detailed-soft-skill-card">
                      <div className="detailed-soft-skill-header">
                        <span className="detailed-soft-skill-name">
                          {skill.name}
                        </span>
                        <div className="detailed-soft-skill-trend">
                          {getTrendIcon(skill.trend)}
                        </div>
                      </div>
                      <div className="detailed-soft-skill-scores">
                        <div className="detailed-soft-skill-score">
                          <span className="detailed-score-label">Current</span>
                          <span className="detailed-score-value">
                            {skill.score}
                          </span>
                        </div>
                        <div className="detailed-soft-skill-score">
                          <span className="detailed-score-label">
                            Benchmark
                          </span>
                          <span className="detailed-score-value">
                            {skill.benchmark}
                          </span>
                        </div>
                      </div>
                      <div className="detailed-soft-skill-bar">
                        <div className="detailed-skill-progress">
                          <div
                            className="detailed-current-bar"
                            style={{ width: `${skill.score}%` }}
                          ></div>
                          <div
                            className="detailed-benchmark-line"
                            style={{ left: `${skill.benchmark}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
              <div className="detailed-soft-skills-summary">
                <div className="detailed-strengths-weaknesses">
                  <div className="detailed-strengths">
                    <h4>Key Strengths</h4>
                    <ul>
                      {analysisData.sections.softSkills?.strengths.map(
                        (strength, index) => (
                          <li key={index}>
                            <CheckCircle
                              size={16}
                              className="detailed-strength-icon"
                            />
                            {strength}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                  <div className="detailed-weaknesses">
                    <h4>Development Areas</h4>
                    <ul>
                      {analysisData.sections.softSkills?.weaknesses.map(
                        (weakness, index) => (
                          <li key={index}>
                            <AlertCircle
                              size={16}
                              className="detailed-weakness-icon"
                            />
                            {weakness}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          );

        case "summary":
          return (
            <div className="detailed-section-content">
              <div className="detailed-summary-container">
                <h3>Executive Summary</h3>
                <div className="detailed-summary-content">
                  <p>{analysisData.sections.summary}</p>
                </div>
                <div className="detailed-recommendations">
                  <h4>Recommended Actions</h4>
                  <div className="detailed-action-items">
                    <div className="detailed-action-item">
                      <div className="detailed-action-icon">
                        <TrendingUp size={20} />
                      </div>
                      <div className="detailed-action-content">
                        <h5>Leadership Development</h5>
                        <p>
                          Enroll in leadership training program to enhance team
                          management skills
                        </p>
                      </div>
                    </div>
                    <div className="detailed-action-item">
                      <div className="detailed-action-icon">
                        <Target size={20} />
                      </div>
                      <div className="detailed-action-content">
                        <h5>Cross-functional Experience</h5>
                        <p>
                          Assign to cross-departmental projects to broaden
                          strategic perspective
                        </p>
                      </div>
                    </div>
                    <div className="detailed-action-item">
                      <div className="detailed-action-icon">
                        <Award size={20} />
                      </div>
                      <div className="detailed-action-content">
                        <h5>Mentorship Program</h5>
                        <p>
                          Pair with senior leader for guidance on innovation and
                          strategic thinking
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );

        default:
          return <div>Section not found</div>;
      }
    };

    return (
      <div className="detailed-comparison-layout">
        {/* Navigation Sidebar */}
        <div className="detailed-navigation">
          <div className="detailed-nav-header">
            <h3>Report Sections</h3>
          </div>
          <nav className="detailed-nav-menu">
            {sections.map((section) => {
              const IconComponent = section.icon;
              return (
                <button
                  key={section.id}
                  className={`detailed-nav-item ${
                    activeSection === section.id ? "active" : ""
                  }`}
                  onClick={() => setActiveSection(section.id)}
                >
                  <IconComponent size={18} />
                  <span>{section.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Main Content */}
        <div className="detailed-content">
          <div className="detailed-content-header">
            <h2>{sections.find((s) => s.id === activeSection)?.label}</h2>
          </div>
          {renderSection()}
        </div>
      </div>
    );
  };

  const handleDownloadPDF = () => {
    // Simulate PDF download
    const element = document.createElement("a");
    const file = new Blob(
      [
        `Detailed Comparison Report\n\n${getReportTitle()}\n\nGenerated on: ${new Date().toLocaleDateString()}\n\nThis is a sample PDF download. In a real implementation, this would generate a comprehensive PDF report with all analysis data.`,
      ],
      { type: "text/plain" }
    );
    element.href = URL.createObjectURL(file);
    element.download = `comparison-report-${Date.now()}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const generateDefaultFilename = () => {
    const date = new Date();
    const dateStr = date.toISOString().split("T")[0]; // YYYY-MM-DD format
    const timeStr = date.toTimeString().split(" ")[0].replace(/:/g, "-"); // HH-MM-SS format
    return `comparison-report-${dateStr}-${timeStr}`;
  };

  const handleSaveReport = () => {
    const defaultName = generateDefaultFilename();
    setFilename(defaultName);
    setShowFilenameModal(true);
  };

  const confirmSaveReport = () => {
    if (!filename.trim()) {
      alert("Please enter a filename");
      return;
    }

    setShowFilenameModal(false);
    setIsSaving(true);

    // Simulate save operation
    setTimeout(() => {
      setIsSaving(false);
      setShowSaveModal(true);
    }, 1500);
  };
  const navigate = useNavigate();
  return (
    <div className="detailed-comparison-page">
      <BackButton />
      {/* <button onClick={() => navigate(-1)}>Back Button</button> */}

      <main className="detailed-comparison-main">
        <div className="container">
          {/* Header */}
          {/*<div className="detailed-comparison-header">
            <div className="detailed-header-content">
              <div className="detailed-header-text">
                <h1>{getReportTitle()}</h1>
                <p>Comprehensive analysis and recommendations</p>
              </div>
              <div className="detailed-header-actions">
                <button className="detailed-action-btn detailed-secondary" onClick={handleDownloadPDF}>
                  <Download size={16} />
                  Download PDF
                </button>
                <button className="detailed-action-btn detailed-primary" onClick={handleSaveReport} disabled={isSaving}>
                  {isSaving ? (
                    <>
                      <RefreshCw size={16} className="spinning" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Save Report
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>*/}

          {renderDetailedReport()}
        </div>
      </main>

      {/* Filename Modal */}
      {showFilenameModal && (
        <Modal
          show={showFilenameModal}
          onClose={() => setShowFilenameModal(false)}
          type="info"
          title="Save Report"
          showButton={false}
        >
          <div className="detailed-filename-modal-content">
            <div className="detailed-form-group">
              <label htmlFor="filename">Report Filename</label>
              <input
                type="text"
                id="filename"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                placeholder="Enter filename..."
                className="detailed-filename-input"
                autoFocus
              />
              <small className="detailed-filename-hint">
                The report will be saved with this name for future reference
              </small>
            </div>
            <div className="detailed-modal-actions">
              <button
                className="detailed-modal-btn detailed-secondary"
                onClick={() => setShowFilenameModal(false)}
              >
                Cancel
              </button>
              <button
                className="detailed-modal-btn detailed-primary"
                onClick={confirmSaveReport}
                disabled={!filename.trim()}
              >
                Save Report
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Save Success Modal */}
      {showSaveModal && (
        <Modal
          show={showSaveModal}
          onClose={() => setShowSaveModal(false)}
          type="success"
          title="Report Saved Successfully!"
          message={`Your comparison report "${filename}" has been saved and can be accessed from your reports dashboard.`}
          showButton={true}
        />
      )}
    </div>
  );
};

export default DetailedComparisonPage;
